import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { useConfig } from "@jti/app-context/useConfig";
import type { ShippingFormFields } from "@jti/commerce-types";
import { Loader } from "@jti/core-components/components/Loader";
import { newrelic } from "@jti/fe-clients";
import {
  DOB_INPUT_DATE,
  getErrorMessageFromError,
  isAddressFormExtendedWithProvince,
  isNonEmptyString,
} from "@jti/fe-common";
import { convertStoredAddressToCasAddressStored, QueryClient, QueryClientProvider } from "@jti/rest-api";
import { useDispatch, useSelector, useStore } from "@jti/store/reduxHooks";
import { checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import {
  useGetProfileQuery,
  useUpdateAgeVerificationMutation,
  useUpdateCustomAttributesMutation,
} from "@jti/store/slices/profileApiSlice";
import type { RootStoreState } from "@jti/store/types";
import type { UseFormReturn } from "@jti/ui";
import { AgeVerificationDialog, formatDate, useTranslations } from "@jti/ui";

import { useCheckoutFastFlowContext } from "@checkout-v2/features/checkoutFastFlow";
import {
  FISCAL_CODE_ATTRIBUTE_KEY,
  ORDER_ATTRIBUTE_FORM_FIELD_IDENTIFIER,
} from "@checkout-v2/features/OrderAttributes/const";
import { shippingMethodFieldFactory } from "@checkout-v2/formFields/factories/shippingMethodCodeFieldFactory";
import { telephoneFieldFactory } from "@checkout-v2/formFields/factories/telephoneFieldFactory";
import type { SubmitHandler } from "@checkout-v2/forms";
import { useGtmShippingInfo } from "@checkout-v2/shared/hooks";
import { useDeliveryOptions } from "@checkout-v2/shared/hooks/useDeliveryOptions";
import { usePhoneValidation } from "@checkout-v2/shared/hooks/usePhoneValidation";
import { useSetBillingAddressOnCart } from "@checkout-v2/shared/hooks/useSetBillingAddressOnCart";
import {
  SET_SHIPPING_ADDRESS_ON_CART_MUTATION_FIXED_CACHE_KEY,
  useSetShippingAddressOnCart,
} from "@checkout-v2/shared/hooks/useSetShippingAddressOnCart";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider/hooks/";
import { useShippingFormDataContext } from "@checkout-v2/shared/providers/ShippingFormDataProvider";
import { hasExternalAddressValidationError } from "@checkout-v2/shared/utils/externalAddressValidationErrorUtils";
import { getMagentoAddress } from "@checkout-v2/shared/utils/getMagentoAddress";

import { useEnsureCartRequestsAreProcessed } from "../hooks/useEnsureCartRequestsAreProcessed";
import { ShippingFormRenderer } from "../renderer/ShippingFormRenderer";
import { constructBillingAddressPayload } from "../utils/constructBillingAddressPayload";

export const ShippingFormOrchestrator = () => {
  const { __, dictionary } = useTranslations();
  const location = useLocation();
  const navigate = useNavigate();
  const config = useConfig();
  const dispatch = useDispatch();
  const store = useStore<RootStoreState>();

  const { isCheckoutFastFlowInProgress, checkoutFlowType, shippingInfoPrefilled } = useCheckoutFastFlowContext();
  const { trackGtmShippingInfo } = useGtmShippingInfo();
  const { initialDefaultValues, formFieldsConfig, isShippingAgeVerificationEnabled, isLoading } =
    useShippingFormDataContext();

  const checkoutV2State = useSelector((state) => state.checkoutV2);

  const checkoutError = checkoutV2State.error;
  const savedFormData = checkoutV2State.data;

  const { setBillingAddressOnCart } = useSetBillingAddressOnCart();
  const { setShippingAddressOnCart, setShippingAddressOnCartState } = useSetShippingAddressOnCart();

  const checkoutConfiguration = useCheckoutV2Configuration();
  const [updateAgeVerification, { isLoading: isAgeVerificationPending }] = useUpdateAgeVerificationMutation();
  const [updateCustomAttributes] = useUpdateCustomAttributesMutation();
  const { validatePhone } = usePhoneValidation();
  const profileQueryStatus = useGetProfileQuery();
  const isAgeVerified = profileQueryStatus.data?.is_age_verified === true;
  const [isAgeVerificationDialogOpened, setIsAgeVerificationDialogOpened] = useState(false);
  const [isAgeVerificationError, setIsAgeVerificationError] = useState(false);
  const [hasCompletedAgeVerification, setHasCompletedAgeVerification] = useState(false);
  const [queryClient] = useState(() => new QueryClient());

  const { deliveryOptionsData, allDeliveryOptions, setShippingMethodMutationState, setShippingMethodOnCart } =
    useDeliveryOptions();
  const billingAddressCardRef = useRef<HTMLDivElement>(null);
  const previousShippingSectionStateRef = useRef(checkoutV2State.sectionsState.shippingSection);

  const { ensureCartRequestsAreProcessed } = useEnsureCartRequestsAreProcessed();

  const formFieldsDefaultValues = useMemo(() => {
    if (!initialDefaultValues) {
      return null;
    }

    if (savedFormData) {
      return {
        ...initialDefaultValues,
        ...savedFormData,
        shippingMethodCode: shippingMethodFieldFactory.getEligibleValue(
          checkoutConfiguration.shippingMethods,
          savedFormData.shippingMethodCode,
          {
            isShippingAgeVerificationEnabled,
            isAgeVerified,
          }
        ),
      };
    }

    return initialDefaultValues;
  }, [
    savedFormData,
    initialDefaultValues,
    checkoutConfiguration.shippingMethods,
    isShippingAgeVerificationEnabled,
    isAgeVerified,
  ]);

  const handlePhoneValidation = async (data: ShippingFormFields, form?: UseFormReturn<ShippingFormFields>) => {
    const phoneNumber = telephoneFieldFactory.toTelephoneString(data.telephone);

    if (!phoneNumber) {
      return;
    }

    const { isValid } = await validatePhone(phoneNumber);

    if (isValid) {
      return;
    }

    const phoneField = config.isInternationalPhoneEnabled ? "telephone.internationalPhone" : "telephone.phone";
    form?.setError(phoneField, {
      type: "server",
      message: __("form_validation.phone_number_pattern"),
    });
    form?.setFocus(phoneField);

    throw new Error("Phone validation failed");
  };

  const handleFiscalCodeValidation = async (data: ShippingFormFields, form?: UseFormReturn<ShippingFormFields>) => {
    const fiscalCode = data.orderAttributes?.[FISCAL_CODE_ATTRIBUTE_KEY];

    if (!isNonEmptyString(fiscalCode)) {
      return;
    }

    try {
      await updateCustomAttributes({ [FISCAL_CODE_ATTRIBUTE_KEY]: fiscalCode.trim() }).unwrap();
    } catch (error) {
      const ORDER_ATTRIBUTE_FIELD_NAME = `${ORDER_ATTRIBUTE_FORM_FIELD_IDENTIFIER}.${FISCAL_CODE_ATTRIBUTE_KEY}`;
      form?.setError(ORDER_ATTRIBUTE_FIELD_NAME, {
        type: "server",
        message: getErrorMessageFromError(error, __("cart.checkout.error.incorrect_fiscal_code"), dictionary),
      });
      form?.setFocus(ORDER_ATTRIBUTE_FIELD_NAME);
      throw new Error("Fiscal code validation failed");
    }
  };

  const setShippingMethod = async (selectedDeliveryOption: string) => {
    const result = await setShippingMethodOnCart(selectedDeliveryOption);

    if (!result.error) {
      return;
    }

    dispatch(
      checkoutV2Slice.actions.setError({
        message: __("cart.checkout.warning.problem_with_shipping_method"),
        type: "server",
      })
    );
    throw result.error;
  };

  const handleSettingShippingMethod = async (data: ShippingFormFields) => {
    if (!data.deliveryOption) {
      dispatch(
        checkoutV2Slice.actions.setError({
          message: __("cart.checkout.shipping_options.validation.required"),
          type: "validation",
          fieldName: "deliveryOption",
        })
      );
      throw new Error("DELIVERY_OPTION_NOT_SELECTED");
    }

    if (
      setShippingMethodMutationState.status === "uninitialized" ||
      deliveryOptionsData?.methodSelected?.methodCode !== data.deliveryOption
    ) {
      return setShippingMethod(data.deliveryOption);
    }

    return;
  };

  const getCurrentSetShippingAddressState = useCallback(() => {
    const currentShippingAddressState =
      store.getState().magentoApi.mutations[SET_SHIPPING_ADDRESS_ON_CART_MUTATION_FIXED_CACHE_KEY];

    return currentShippingAddressState;
  }, [store]);

  const checkShippingAddressIsSet = (): void => {
    const currentShippingAddressState = getCurrentSetShippingAddressState();

    if (!currentShippingAddressState || currentShippingAddressState.error) {
      const errorMessage = __("cart.checkout.error.shipping_address_not_specified");

      dispatch(
        checkoutV2Slice.actions.setError({
          message: errorMessage,
          type: "validation",
          fieldName: formFieldsConfig?.addressHomeDelivery.name,
        })
      );

      throw new Error("SHIPPING_ADDRESS_NOT_SET");
    }

    const isSetShippingAddressSuccess =
      currentShippingAddressState.status === "fulfilled" && !currentShippingAddressState.error;

    if (isSetShippingAddressSuccess) {
      return;
    }

    dispatch(
      checkoutV2Slice.actions.setError({
        message: __("cart.checkout.error.shipping_address_not_specified"),
        type: "validation",
        fieldName: formFieldsConfig?.addressHomeDelivery.name,
      })
    );

    throw new Error("SHIPPING_ADDRESS_NOT_SET");
  };

  const checkBillingAddressIsSet = (data: ShippingFormFields): void => {
    try {
      constructBillingAddressPayload(
        data,
        checkoutConfiguration,
        config.isBillingAddressVisibleForCheckout,
        data.shouldUseProfileData ? formFieldsConfig?.telephone.savedDefaultValue : undefined,
        formFieldsConfig?.billingAddress.defaultAddress
      );
    } catch {
      dispatch(
        checkoutV2Slice.actions.setError({
          message: __("cart.checkout.error.billing_address_not_specified"),
          type: "validation",
          fieldName: "billingAddress",
        })
      );

      throw new Error("BILLING_ADDRESS_NOT_SET");
    }
  };

  const handleSettingBillingAddress = async (data: ShippingFormFields) => {
    const billingAddress = constructBillingAddressPayload(
      data,
      checkoutConfiguration,
      config.isBillingAddressVisibleForCheckout,
      data.shouldUseProfileData ? formFieldsConfig?.telephone.savedDefaultValue : undefined,
      formFieldsConfig?.billingAddress.defaultAddress
    );

    if (isAddressFormExtendedWithProvince(config.addressFormVariant)) {
      billingAddress.regionCode = profileQueryStatus.data?.extra?.province ?? "";
    }

    const setBillingAddressOnCartMutationResult = await setBillingAddressOnCart(billingAddress);

    if (setBillingAddressOnCartMutationResult.error) {
      if (hasExternalAddressValidationError(setBillingAddressOnCartMutationResult.error)) {
        if (billingAddressCardRef.current) {
          billingAddressCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }

        throw setBillingAddressOnCartMutationResult.error;
      }

      dispatch(
        checkoutV2Slice.actions.setError({
          message: __("cart.checkout.warning.problem_with_billing_address"),
          type: "server",
        })
      );

      throw setBillingAddressOnCartMutationResult.error;
    }

    return;
  };

  const handleSettingShippingAddressWithContactDetails = async (data: ShippingFormFields) => {
    const isProfilePhoneNumberValid = formFieldsConfig?.telephone.isPhoneNumberValid ?? true;

    if (data.shouldUseProfileData && isProfilePhoneNumberValid) {
      return;
    }

    const selectedShippingMethodType = shippingMethodFieldFactory.getSelectedShippingMethodType(
      data.shippingMethodCode,
      checkoutConfiguration
    );

    const addressByShippingMethodType = {
      atHome: data.addressHomeDelivery,
      "custom delivery": data.addressCustomShipping,
      "in-store delivery": data.addressInStoreDelivery,
    };

    const selectedAddressByShippingMethod = selectedShippingMethodType
      ? addressByShippingMethodType[selectedShippingMethodType]
      : null;

    const magentoAddress = getMagentoAddress({
      methodType: selectedShippingMethodType,
      address: selectedAddressByShippingMethod,
      countryCode: config.ISO_COUNTRY_CODE,
    });

    if (!magentoAddress) {
      return;
    }

    try {
      await setShippingAddressOnCart({
        ...magentoAddress,
        firstname: data.firstName,
        lastname: data.lastName,
        telephone: telephoneFieldFactory.toTelephoneString(data.telephone),
      });
    } catch (error) {
      dispatch(
        checkoutV2Slice.actions.setError({
          message: __("cart.checkout.error.shipping_address_not_specified"),
          type: "server",
          fieldName: formFieldsConfig?.addressHomeDelivery.name,
        })
      );

      throw error;
    }
  };

  const onSubmit: SubmitHandler<ShippingFormFields> = async (data, form) => {
    dispatch(checkoutV2Slice.actions.clearError());

    try {
      await ensureCartRequestsAreProcessed();
    } catch (error) {
      newrelic.noticeError(error as Error);
      return;
    }

    if (!form) {
      dispatch(
        checkoutV2Slice.actions.setError({
          message: __("cart.checkout.error.unexpected_error"),
          type: "validation",
        })
      );
      return;
    }

    try {
      checkShippingAddressIsSet();
      checkBillingAddressIsSet(data);

      await Promise.all([
        handleSettingShippingAddressWithContactDetails(data),
        handleFiscalCodeValidation(data, form),
        handlePhoneValidation(data, form),
        handleSettingShippingMethod(data),
        handleSettingBillingAddress(data),
      ]);
    } catch (error) {
      newrelic.noticeError(error as Error);
      return;
    }

    const selectedShippingMethod = allDeliveryOptions.find((option) => option.methodCode === data.deliveryOption);

    if (selectedShippingMethod) {
      trackGtmShippingInfo(selectedShippingMethod, checkoutFlowType, shippingInfoPrefilled);
    }

    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "completed",
        paymentSection: "active",
        orderConfirmationSection: "rested",
      })
    );
    dispatch(checkoutV2Slice.actions.setData(data));
  };

  useEffect(
    function clearErrorWhenEnteringShippingEditMode() {
      const hasShippingAddressError = checkoutError?.fieldName === formFieldsConfig?.addressHomeDelivery.name;
      const currentShippingSectionState = checkoutV2State.sectionsState.shippingSection;
      const wasShippingSectionEditing = previousShippingSectionStateRef.current === "editing";
      const isShippingSectionEditing = currentShippingSectionState === "editing";
      const didEnterShippingEditMode = !wasShippingSectionEditing && isShippingSectionEditing;

      if (hasShippingAddressError && didEnterShippingEditMode) {
        dispatch(checkoutV2Slice.actions.clearError());
      }

      previousShippingSectionStateRef.current = currentShippingSectionState;
    },
    [dispatch, formFieldsConfig, checkoutError, checkoutV2State.sectionsState.shippingSection]
  );

  if (!formFieldsConfig || !formFieldsDefaultValues || isCheckoutFastFlowInProgress || isLoading) {
    return <Loader data-testid="shippingFormOrchestratorLoader" />;
  }

  const handleShippingMethodChange = () => {
    setShippingAddressOnCartState.reset();
  };

  const handleVerifyAge = () => {
    setIsAgeVerificationError(false);
    setIsAgeVerificationDialogOpened(true);
  };

  const handleLoadAgeVerificationDataFromCache = () => {
    setIsAgeVerificationDialogOpened(true);
  };

  const handleAgeVerificationSuccess = async (avpUuid?: string) => {
    if (!avpUuid) {
      setIsAgeVerificationError(true);
      return;
    }

    try {
      const response = await updateAgeVerification(avpUuid).unwrap();

      if (response.is_age_verified) {
        setHasCompletedAgeVerification(true);
        setIsAgeVerificationDialogOpened(false);
        return;
      }

      setIsAgeVerificationError(true);
    } catch {
      setIsAgeVerificationError(true);
    }
  };

  return (
    <>
      <ShippingFormRenderer
        formFieldsConfig={formFieldsConfig}
        defaultValues={formFieldsDefaultValues}
        billingAddressCardRef={billingAddressCardRef}
        onShippingMethodChange={handleShippingMethodChange}
        isShippingAgeVerificationEnabled={isShippingAgeVerificationEnabled}
        isAgeVerified={isAgeVerified}
        hasCompletedAgeVerification={hasCompletedAgeVerification}
        isAgeVerificationError={isAgeVerificationError}
        isAgeVerificationPending={isAgeVerificationPending}
        onVerifyAge={handleVerifyAge}
        onSubmit={onSubmit}
      />
      <QueryClientProvider client={queryClient}>
        <AgeVerificationDialog
          isOpened={isAgeVerificationDialogOpened}
          onLoadDataFromCache={handleLoadAgeVerificationDataFromCache}
          isPending={isAgeVerificationPending}
          description={checkoutConfiguration.ageVerificationPopup?.description ?? ""}
          userData={{
            email: profileQueryStatus.data?.personalInfo.email ?? "",
            firstname: profileQueryStatus.data?.personalInfo.firstname,
            lastname: profileQueryStatus.data?.personalInfo.lastname,
            phone: profileQueryStatus.data?.personalInfo.telephone,
            dateOfBirth: formatDate(profileQueryStatus.data?.personalInfo.dob, DOB_INPUT_DATE),
            addresses: profileQueryStatus.data?.addresses?.map(convertStoredAddressToCasAddressStored),
          }}
          onClose={() => setIsAgeVerificationDialogOpened(false)}
          onSuccess={handleAgeVerificationSuccess}
          navigation={{
            search: location.search,
            replaceSearch: (search) =>
              navigate(
                {
                  pathname: location.pathname,
                  search,
                  hash: location.hash,
                },
                { replace: true }
              ),
          }}
        />
      </QueryClientProvider>
    </>
  );
};
